import { useMemo, useState, useEffect, useCallback, useRef } from 'react';
import { useForm, Controller } from 'react-hook-form';
import classNames from 'classnames/bind';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import RegisterFormInput from 'components/RegisterFormInput/RegisterFormInput';
import FormSelect from 'components/FormSelect/FormSelect';
import { useAppSelector } from 'app/hooks';
import { selectCurrentMarket } from 'features/core/selectors';
import { IVerifyIdentityFormModel } from 'features/payment/types';
import styles from './DebtSection.module.scss';
import AlreadyRegisteredPanel from 'templates/VerifyIdentityWithoutDebtReferencePageData/components/AlreadyRegisteredPanel/AlreadyRegisteredPanel';
import RegistrationDetailsIncorrectPanel from 'templates/VerifyIdentityWithoutDebtReferencePageData/components/RegistrationDetailsIncorrectPanel/RegistrationDetailsIncorrectPanel';
import { format } from 'date-fns';
import ShowUnknownErrorPanel from '../ShowUnknownErrorPanel/ShowUnknownErrorPanel';
import { formProgression } from 'common/helpers/Gtm';
import CustomerNotAuthorized from '../CustomerNotAuthorized/CustomerNotAuthorized';
import TagManager from 'react-gtm-module';
import { Permissions } from 'common/consts/customerPermissions';
import { RegexNumber } from 'common/helpers/RegexNumber';
import useOnClickOutside from 'common/hooks/useOnClickOutside';
import { validateDOBPayload } from 'common/helpers/DateofBirthValidation';

type Props = {
  Model?: any;
  PageData?: any;
  LayoutModel?: any;
  onVerifyIdentity: (data: IVerifyIdentityFormModel) => void;
};

/**
 * Helper function to fill select with numeric values
 * @param from
 * @param to
 * @param defaultValue
 * @returns
 */
export const getNumericOptionsArray = (
  from: number,
  to: number,
  defaultValue?: string,
  isorderDesc?: boolean,
) => {
  const options: any[] = [];
  if (isorderDesc) {
    for (let i = to; i >= from; i--) {
      if (i <= 9) {
        options.push('0' + i);
      } else {
        options.push(i);
      }
    }
  } else {
    for (let i = from; i <= to; i++) {
      if (i <= 9) {
        options.push('0' + i);
      } else {
        options.push(i);
      }
    }
  }

  if (defaultValue) {
    options.unshift(defaultValue);
  }
  return options;
};

const DebtSection = (props: Props) => {
  const { executeRecaptcha } = useGoogleReCaptcha(); // Recaptcha v3
  const { Model, PageData, LayoutModel, onVerifyIdentity } = props;
  const [showHiddenInput, setShowHiddenInput] = useState<Boolean>(false);
  const [isNotNullish, setIsNotNullish] = useState<Boolean>(false);
  const [showTermsAndConditionsLink, setShowTermsAndConditionsLink] =
    useState<Boolean>(false);
  const [showAdditionalConsent, setShowAdditionalConsent] =
    useState<Boolean>(false);
  const [disableContinueButton, setDisableContinueButton] =
    useState<Boolean>(true);
  const [showAlreadyRegisteredPanel, setShowAlreadyRegisteredPanel] =
    useState<Boolean>(
      Model?.VerifyIdentityViewModelWithoutReference.ShowAlreadyRegisteredPanel,
    );

  const [showCustomerNotAuthorizedPanel, setShowCustomerNotAuthorizedPanel] =
    useState<Boolean>(
      Model?.IsPortalAccessDenied ||
      Model?.IsPortalAccessDeniedWithSinglePayPer,
    );
  const [
    showRegistrationDetailsIncorrect,
    setShowRegistrationDetailsIncorrect,
  ] = useState<Boolean>(
    Model?.VerifyIdentityViewModelWithoutReference
      .RegistrationDetailsIncorrectPanel,
  );
  const [showUnknownErrorPanel, setShowUnknownErrorPanel] = useState<Boolean>(
    Model?.VerifyIdentityViewModelWithoutReference.ShowUnknownErrorPanel,
  );

  const [width, setWidth] = useState<number>(window.innerWidth);

  const {
    control,
    register,
    setValue,
    getValues,
    handleSubmit,
    watch,
    formState: { errors: errors },
  } = useForm();

  useEffect(() => {
    setShowRegistrationDetailsIncorrect(
      Model?.VerifyIdentityViewModelWithoutReference
        .RegistrationDetailsIncorrectPanel,
    );
    setShowAlreadyRegisteredPanel(
      Model?.VerifyIdentityViewModelWithoutReference.ShowAlreadyRegisteredPanel,
    );
    setShowCustomerNotAuthorizedPanel(
      Model?.IsPortalAccessDenied ||
      Model?.IsPortalAccessDeniedWithSinglePayPer,
    );
    setShowUnknownErrorPanel(
      Model?.VerifyIdentityViewModelWithoutReference.ShowUnknownErrorPanel,
    );
  }, [Model]);

  useEffect(() => {
    if (
      (Model?.IsPortalAccessDenied ||
        Model?.IsPortalAccessDeniedWithSinglePayPer) &&
      showCustomerNotAuthorizedPanel
    ) {
      var tagManagerArgs;
      if (Model?.IsPortalAccessDeniedWithSinglePayPer) {
        tagManagerArgs = {
          dataLayer: {
            event: 'form_error',
            checkout_name: 'Registration',
            error_message: 'No Auth - Partial',
          },
        };
        TagManager.dataLayer(tagManagerArgs);
      }
      if (Model?.IsPortalAccessDenied) {
        tagManagerArgs = {
          dataLayer: {
            event: 'form_error',
            checkout_name: 'Registration',
            error_message: 'No Auth - Full',
          },
        };
        TagManager.dataLayer(tagManagerArgs);
      }
    }
  }, [
    Model?.IsPortalAccessDenied,
    Model?.IsPortalAccessDeniedWithSinglePayPer,
    showCustomerNotAuthorizedPanel,
  ]);

  useEffect(() => {
    if (
      Model?.VerifyIdentityViewModelWithoutReference
        ?.RegistrationDetailsIncorrectPanel
    ) {
      var tagManagerArgs = {
        dataLayer: {
          event: 'form_error',
          checkout_name: 'Registration',
          error_message: 'Incorrect details',
        },
      };
      TagManager.dataLayer(tagManagerArgs);
    }
  }, [
    Model?.VerifyIdentityViewModelWithoutReference
      ?.RegistrationDetailsIncorrectPanel,
  ]);
  const currentMarket = useAppSelector(selectCurrentMarket);
  const watchAllFields = watch();

  const daysOptions = useMemo(() => {
    return getNumericOptionsArray(
      1,
      31,
      Model?.VerifyIdentityViewModelWithoutReference?.PageData?.DayPlaceholder,
      false,
    );
  }, [Model?.VerifyIdentityViewModelWithoutReference?.PageData]);

  const monthOptions = useMemo(() => {
    return getNumericOptionsArray(
      1,
      12,
      Model?.VerifyIdentityViewModelWithoutReference?.PageData
        ?.MonthPlaceholder,
      false,
    );
  }, [Model?.VerifyIdentityViewModelWithoutReference?.PageData]);

  const yearsOptions = useMemo(() => {
    const currentYear = new Date().getFullYear();
    return getNumericOptionsArray(
      1900,
      currentYear,
      Model?.VerifyIdentityViewModelWithoutReference?.PageData?.YearPlaceholder,
      true,
    );
  }, [Model?.VerifyIdentityViewModelWithoutReference?.PageData]);

  const [showDobValidation, setShowDobValidation] = useState<Boolean>(
    false,
  );
  const [hasDOBError, setHasDOBError] = useState<Boolean>(
    false,
  );

  const handleDobMobileChange = (e: any, callback: Function) => {
    DateOfBirthMobileUpdate(e);
    callback(e.target.value);
  };

  const handleClickOutside = () => {
    setShowDobValidation(true);
  };
  const DobRef = useRef(null);

  useOnClickOutside(DobRef, handleClickOutside);

  const onSubmit = async (data: IVerifyIdentityFormModel, e: any) => {
    if (!isNotNullish) {
      var elements = e.target.elements;
      var formLength = elements.length;
      for (var i = 0; i < formLength; i++) {
        var element = elements[i];
        if (
          element.value === undefined ||
          element.value === null ||
          element.value === ''
        ) {
          if (window.innerWidth <= 768) {
            if (
              element.name === 'VerifyIdentityFormModel.DateOfBirthDay' ||
              element.name === 'VerifyIdentityFormModel.DateOfBirthMonth' ||
              element.name === 'VerifyIdentityFormModel.DateOfBirthYear'
            ) {
              continue;
            }
          }
          if (element.type === 'hidden') {
            continue;
          } else {
            element.focus();
            break;
          }
        }
      }

      e.preventDefault();
      return;
    }

    if (
      Model?.VerifyIdentityViewModelWithoutReference?.FormModel
        ?.IsDisableRecaptcha
    ) {
      onVerifyIdentity(data);
    } else {
      if (!executeRecaptcha) {
        return;
      }
      const token = await executeRecaptcha();
      if (token) {
        // Add recaptcha response to VerifyIdentityFormModel
        data.VerifyIdentityFormModel = {
          ...data.VerifyIdentityFormModel,
          RecaptchaResponseToken: token,
        };
        onVerifyIdentity(data);
      } else {
        onVerifyIdentity(data);
      }
    }
  };
  const day = (daysOptions.length - 1);
  const month = (monthOptions.length - 1);
  const currentYear = new Date().getFullYear();

  const setValuesWhenIsDirty = (name: string, value: string) => {
    setIsNotNullish(true);
    setShowAdditionalConsent(false);
    setShowTermsAndConditionsLink(false);
    setDisableContinueButton(true);
    setHasDOBError(false);
    if (watchAllFields) {
      let { IsDisableRecaptcha, AcceptContactDetailsConsent, ...newPayload } =
        watchAllFields.VerifyIdentityFormModel;
      if (width <= 768) {
        let {
          DateOfBirthDay,
          DateOfBirthMonth,
          DateOfBirthYear,
          ...newPayload1
        } = newPayload;
        newPayload = newPayload1;
      } else {
        let { DateOfBirth, ...newPayload1 } = newPayload;
        newPayload = newPayload1;
      }
      Object.values(newPayload).map((objValue) => {
        if (objValue === undefined || objValue === null || objValue === '') {
          setIsNotNullish(false);
        }
      });
      if (
        (newPayload?.DateOfBirthDay != undefined &&
          isNaN(parseInt(newPayload?.DateOfBirthDay))) ||
        (newPayload?.DateOfBirthMonth != undefined &&
          isNaN(parseInt(newPayload?.DateOfBirthMonth))) ||
        (newPayload?.DateOfBirthYear != undefined &&
          isNaN(newPayload?.DateOfBirthYear))
      ) {
        setIsNotNullish(false);
        setHasDOBError(true);
      }
      if(validateDOBPayload(newPayload, day, month, currentYear))
      {
        setIsNotNullish(false);
        setHasDOBError(true);
      }
    }
    if (isNotNullish) {
      setShowAdditionalConsent(true);
      setShowTermsAndConditionsLink(true);
      setDisableContinueButton(false);
    }
  };

  function DateOfBirthMobileUpdate(e: any) {
    var mobileDateValue: Date;
    if (e != null || e != undefined) {
      var datemob = e.target.value;
      mobileDateValue = new Date(datemob);
    } else {
      mobileDateValue = new Date(
        getValues('VerifyIdentityFormModel.DateOfBirth'),
      );
    }

    if (!isNaN(mobileDateValue.getTime())) {
      let mobileDay = parseInt(mobileDateValue.getDate().toString());
      var mobileMonth = parseInt(mobileDateValue.getMonth().toString()) + 1;
      var mobileYear = parseInt(mobileDateValue.getFullYear().toString());
      setValue('VerifyIdentityFormModel.DateOfBirthDay', mobileDay);
      setValue('VerifyIdentityFormModel.DateOfBirthMonth', mobileMonth);
      setValue('VerifyIdentityFormModel.DateOfBirthYear', mobileYear);
    }
  }

  function DateOfBirthDesktopUpdate() {
    let day: number = getValues('VerifyIdentityFormModel.DateOfBirthDay');
    let month: number = getValues('VerifyIdentityFormModel.DateOfBirthMonth');
    let year: number = getValues('VerifyIdentityFormModel.DateOfBirthYear');

    if (day > 0 && month >= 0 && year > 0) {
      var dateOfBirth =
        year +
        '-' +
        (month < 10 ? '0' : '') +
        month +
        '-' +
        (day < 10 ? '0' : '') +
        day;
      setValue('VerifyIdentityFormModel.DateOfBirth', dateOfBirth);
    }
  }

  function handleWindowSizeChange() {
    setWidth(window.innerWidth);

    if (window.innerWidth <= 768) {
      DateOfBirthDesktopUpdate();
      return;
    } else {
      DateOfBirthMobileUpdate(null);
      return;
    }
  }

  useEffect(() => {
    window.addEventListener('resize', handleWindowSizeChange);
    return () => {
      window.removeEventListener('resize', handleWindowSizeChange);
    };
  }, [
    width,
    'VerifyIdentityFormModel.DateOfBirth',
    'VerifyIdentityFormModel.DateOfBirthDay',
    'VerifyIdentityFormModel.DateOfBirthMonth',
    'VerifyIdentityFormModel.DateOfBirthYear',
  ]);

  const handleChangeDate = (e: any, callback: Function) => {
    if (e.target.value.length >= 2 || e.target.value == 'e') {
      if (
        e.target.value <= day &&
        e.target.value !== '00' &&
        e.target.value !== '000'
      ) {
        e.target.parentNode.nextSibling.firstElementChild.focus();
      }

      e.target.value = e.target.value.slice(0, 2);
    }
    callback(e);
  };

  const handleChangeMonth = (e: any, callback: Function) => {
    if (e.target.value) {
      if (
        e.target.value.length >= 2 &&
        e.target.value !== '00' &&
        e.target.value !== '000'
      ) {
        if (e.target.value <= month) {
          e.target.parentNode.nextSibling.firstElementChild.focus();
        }
      }
      e.target.value = e.target.value.slice(0, 2);
    }
    callback(e);
  };
  const handleChangeYear = (e: any, callback: Function) => {
    if (e.target.value) {
      if (
        e.target.value.length >= 4 &&
        e.target.value !== '00' &&
        e.target.value !== '000'
      ) {
        if (e.target.value >= 1900 && e.target.value <= currentYear) {
          setDisableContinueButton(false);
        }
      }
      e.target.value = e.target.value.slice(0, 4);
    }
    callback(e);
  };



  return (
    <div>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className={styles.wrapper}
        id="editVerifyIdentityForm"
      >
        <div className="registrationjounery_container">
          <div className="auth0-container">
            <div className="form-heading">
              {
                Model?.VerifyIdentityViewModelWithoutReference?.PageData
                  ?.ConfirmYourDetailsPageHeader
              }
            </div>
            {Model.ShowNameSurNameDobSection && (
              <>
                <div>
                  <Controller
                    control={control}
                    name={'VerifyIdentityFormModel.Name'}
                    render={({
                      field: { onChange, name, value },
                      fieldState: { isDirty },
                    }) => {
                      if (isDirty) {
                        setValuesWhenIsDirty(name, value);
                        setShowHiddenInput(true);
                      }

                      return (
                        <>
                          <RegisterFormInput
                            name={name}
                            value={value}
                            label={
                              Model?.VerifyIdentityViewModelWithoutReference
                                ?.PageData?.NameLabel
                            }
                            placeholder={
                              Model?.VerifyIdentityViewModelWithoutReference
                                ?.PageData?.NamePlaceholderText
                            }
                            onChange={onChange}
                            className={classNames(
                              isDirty === true && value.length > 0 && 'valid',
                            )}
                            inputclassName={classNames(
                              Model?.VerifyIdentityViewModelWithoutReference
                                ?.FormModel.NameHasError && styles.hasError,
                            )}
                            onBlur={(e) =>
                              formProgression(e, 'Registration', 'Name')
                            }
                          />
                          <input
                            type="hidden"
                            {...register(
                              'VerifyIdentityFormModel.IsDisableRecaptcha',
                            )}
                            value={
                              Model?.VerifyIdentityViewModelWithoutReference
                                ?.FormModel?.IsDisableRecaptcha
                            }
                          />
                        </>
                      );
                    }}
                  />
                  {Model?.VerifyIdentityViewModelWithoutReference?.FormModel
                    .NameHasError && (
                      <span className="Text--small text-danger">
                        {
                          Model?.VerifyIdentityViewModelWithoutReference?.PageData
                            ?.NameErrorMessage
                        }
                      </span>
                    )}
                </div>
                <div>
                  <Controller
                    control={control}
                    name={'VerifyIdentityFormModel.Surname'}
                    render={({
                      field: { onChange, name, value },
                      fieldState: { isDirty },
                    }) => {
                      if (isDirty) {
                        setValuesWhenIsDirty(name, value);
                        setShowHiddenInput(true);
                      }

                      return (
                        <>
                          <RegisterFormInput
                            name={name}
                            value={value}
                            label={
                              Model?.VerifyIdentityViewModelWithoutReference
                                ?.PageData?.SurnameLabel
                            }
                            placeholder={
                              Model?.VerifyIdentityViewModelWithoutReference
                                ?.PageData?.SurnamePlaceholderText
                            }
                            onChange={onChange}
                            className={classNames(
                              isDirty === true && value.length > 0 && 'valid',
                            )}
                            onBlur={(e) =>
                              formProgression(e, 'Registration', 'Surname')
                            }
                          />
                          <input
                            type="hidden"
                            {...register(
                              'VerifyIdentityFormModel.IsDisableRecaptcha',
                            )}
                            value={
                              Model?.VerifyIdentityViewModelWithoutReference
                                ?.FormModel?.IsDisableRecaptcha
                            }
                          />
                        </>
                      );
                    }}
                  />
                </div>

                <div>
                  <div
                    className={
                      'Form-SelectWrapper--multi ' + styles.desktopDatePicker
                    }
                  >
                    <div className="form-group">
                      <label>
                        {
                          Model?.VerifyIdentityViewModelWithoutReference
                            ?.PageData?.DateofBirth
                        }
                      </label>
                      <div className={styles.selects} ref={DobRef}>
                        <Controller
                          control={control}
                          name={'VerifyIdentityFormModel.DateOfBirthDay'}
                          render={({
                            field: { onChange, name, value },
                            fieldState: { isDirty },
                          }) => {
                            if (isDirty) {
                              setValuesWhenIsDirty(name, value);
                            }
                            return (
                              <RegisterFormInput
                                type="number"
                                name={name}
                                value={value}
                                label={''}
                                placeholder={PageData?.DayPlaceholder}
                                onChange={(e) => handleChangeDate(e, onChange)}
                                onKeyPress={(e) => RegexNumber(e)}
                                className={classNames(value != null)}
                                max={day.toString()}
                                onBlur={(e) =>
                                  formProgression(
                                    e,
                                    'Registration',
                                    'DateOfBirthDay',
                                  )
                                }
                              />
                            );
                          }}
                        />
                        <Controller
                          control={control}
                          name={'VerifyIdentityFormModel.DateOfBirthMonth'}
                          render={({
                            field: { onChange, name, value },
                            fieldState: { isDirty },
                          }) => {
                            if (isDirty) {
                              setValuesWhenIsDirty(name, value);
                            }
                            return (
                              <RegisterFormInput
                                type="number"
                                name={name}
                                value={value}
                                label={''}
                                placeholder={PageData?.MonthPlaceholder}
                                onChange={(e) => handleChangeMonth(e, onChange)}
                                onKeyPress={(e) => RegexNumber(e)}
                                className={classNames(value != null)}
                                max={month.toString()}
                                onBlur={(e) =>
                                  formProgression(
                                    e,
                                    'Registration',
                                    'DateOfBirthMonth',
                                  )
                                }
                              />
                            );
                          }}
                        />
                        <Controller
                          control={control}
                          name={'VerifyIdentityFormModel.DateOfBirthYear'}
                          render={({
                            field: { onChange, name, value },
                            fieldState: { isDirty },
                          }) => {
                            if (isDirty) {
                              setValuesWhenIsDirty(name, value);
                            }
                            return (
                              <RegisterFormInput
                                type="number"
                                name={name}
                                value={value}
                                label={''}
                                placeholder={PageData?.YearPlaceholder}
                                onChange={(e) => handleChangeYear(e, onChange)}
                                onKeyPress={(e) => RegexNumber(e)}
                                className={classNames(value != null)}
                                max={currentYear.toString()}
                                min={1900}
                                onBlur={(e) =>
                                  formProgression(
                                    e,
                                    'Registration',
                                    'DateOfBirthYear',
                                  )
                                }
                              />
                            );
                          }}
                        />
                      </div>
                      {(showDobValidation == true && isNotNullish == false && hasDOBError == true) &&
                        (<p className='error'>{Model?.VerifyIdentityViewModelWithoutReference
                          ?.PageData?.InvalidDateErrorText}</p>)}
                    </div>
                  </div>

                  <div className={styles.mobiledatapicker}>
                    <Controller
                      control={control}
                      name={'VerifyIdentityFormModel.DateOfBirth'}
                      render={({
                        field: { onChange, name, value },
                        fieldState: { isDirty },
                      }) => {
                        if (isDirty) {
                          setValuesWhenIsDirty(name, value);
                          setShowHiddenInput(true);
                        }

                        return (
                          <>
                            <RegisterFormInput
                              type="date"
                              name={name}
                              value={value}
                              label={
                                Model?.VerifyIdentityViewModelWithoutReference
                                  ?.PageData?.DateOfBirthLabel
                              }
                              placeholder="DD/MM/YYYY"
                              onChange={(e) =>
                                handleDobMobileChange(e, onChange)
                              }
                              max={format(new Date(Date.now()), 'yyyy-MM-dd')}
                              pattern="[0-9]*"
                              className={classNames(styles.widthfull)}
                              onBlur={(e) =>
                                formProgression(
                                  e,
                                  'Registration',
                                  'DateOfBirth',
                                )
                              }
                            />
                          </>
                        );
                      }}
                    />
                  </div>
                </div>
              </>
            )}
            {Model.ShowNameSurNameDobSection &&
              Model.IsPostalEnabledForDuplicateRecord && (
                <div>
                  <Controller
                    control={control}
                    name={'VerifyIdentityFormModel.PostalCode'}
                    render={({
                      field: { onChange, name, value },
                      fieldState: { isDirty },
                    }) => {
                      if (isDirty) {
                        setValuesWhenIsDirty(name, value);
                        setShowHiddenInput(true);
                      }

                      return (
                        <>
                          <RegisterFormInput
                            name={name}
                            value={value}
                            label={
                              Model?.VerifyIdentityViewModelWithoutReference
                                ?.PageData?.PostalCodeLabel
                            }
                            placeholder={
                              Model?.VerifyIdentityViewModelWithoutReference
                                ?.PageData?.PostalCodePlaceholderText
                            }
                            onChange={onChange}
                            className={classNames(
                              isDirty === true && value.length > 0 && 'valid',
                            )}
                            inputclassName={classNames(
                              Model?.VerifyIdentityViewModelWithoutReference
                                ?.FormModel.SurnameHasError && styles.hasError,
                            )}
                            onBlur={(e) =>
                              formProgression(e, 'Registration', 'PostalCode')
                            }
                          />
                        </>
                      );
                    }}
                  />
                  {Model?.VerifyIdentityViewModelWithoutReference?.FormModel
                    .SurnameHasError && (
                      <span className="Text--small text-danger">
                        {
                          Model?.VerifyIdentityViewModelWithoutReference?.PageData
                            ?.SurnameErrorMessage
                        }
                      </span>
                    )}
                </div>
              )}
            {Model.ShowNameSurNameDobSection &&
              Model.IsCityEnabledForDuplicateRecord && (
                <div>
                  <Controller
                    control={control}
                    name={'VerifyIdentityFormModel.CityName'}
                    render={({
                      field: { onChange, name, value },
                      fieldState: { isDirty },
                    }) => {
                      if (isDirty) {
                        setValuesWhenIsDirty(name, value);
                        setShowHiddenInput(true);
                      }

                      return (
                        <>
                          <RegisterFormInput
                            name={name}
                            value={value}
                            label={
                              Model?.VerifyIdentityViewModelWithoutReference
                                ?.PageData?.CityLabel
                            }
                            placeholder={
                              Model?.VerifyIdentityViewModelWithoutReference
                                ?.PageData?.CityErrorMessage
                            }
                            onChange={onChange}
                            className={classNames(
                              isDirty === true && value.length > 0 && 'valid',
                            )}
                            inputclassName={classNames(
                              Model?.VerifyIdentityViewModelWithoutReference
                                ?.FormModel.CityNameHasError && styles.hasError,
                            )}
                            onBlur={(e) =>
                              formProgression(e, 'Registration', 'CityName')
                            }
                          />
                        </>
                      );
                    }}
                  />
                  {Model?.VerifyIdentityViewModelWithoutReference?.FormModel
                    .CityNameHasError && (
                      <span className="Text--small text-danger">
                        {
                          Model?.VerifyIdentityViewModelWithoutReference?.PageData
                            ?.CityErrorMessage
                        }
                      </span>
                    )}
                </div>
              )}
            {Model.ShowNameSurNameDniSection && (
              <>
                <div>
                  <Controller
                    control={control}
                    name={'VerifyIdentityFormModel.Name'}
                    render={({
                      field: { onChange, name, value },
                      fieldState: { isDirty },
                    }) => {
                      if (isDirty) {
                        setValuesWhenIsDirty(name, value);
                        setShowHiddenInput(true);
                      }

                      return (
                        <>
                          <RegisterFormInput
                            name={name}
                            value={value}
                            label={
                              Model?.VerifyIdentityViewModelWithoutReference
                                ?.PageData?.NameLabel
                            }
                            placeholder={
                              Model?.VerifyIdentityViewModelWithoutReference
                                ?.PageData?.NamePlaceholderText
                            }
                            onChange={onChange}
                            className={classNames(
                              isDirty === true && value.length > 0 && 'valid',
                            )}
                            inputclassName={classNames(
                              Model?.VerifyIdentityViewModelWithoutReference
                                ?.FormModel.NameHasError && styles.hasError,
                            )}
                            onBlur={(e) =>
                              formProgression(e, 'Registration', 'Name')
                            }
                          />
                          <input
                            type="hidden"
                            {...register(
                              'VerifyIdentityFormModel.IsDisableRecaptcha',
                            )}
                            value={
                              Model?.VerifyIdentityViewModelWithoutReference
                                ?.FormModel?.IsDisableRecaptcha
                            }
                          />
                        </>
                      );
                    }}
                  />
                  {Model?.VerifyIdentityViewModelWithoutReference?.FormModel
                    .NameHasError && (
                      <span className="Text--small text-danger">
                        {
                          Model?.VerifyIdentityViewModelWithoutReference?.PageData
                            ?.NameErrorMessage
                        }
                      </span>
                    )}
                </div>
                <div>
                  <Controller
                    control={control}
                    name={'VerifyIdentityFormModel.Surname'}
                    render={({
                      field: { onChange, name, value },
                      fieldState: { isDirty },
                    }) => {
                      if (isDirty) {
                        setValuesWhenIsDirty(name, value);
                        setShowHiddenInput(true);
                      }

                      return (
                        <>
                          <RegisterFormInput
                            name={name}
                            value={value}
                            label={
                              Model?.VerifyIdentityViewModelWithoutReference
                                ?.PageData?.SurnameLabel
                            }
                            placeholder={
                              Model?.VerifyIdentityViewModelWithoutReference
                                ?.PageData?.SurnamePlaceholderText
                            }
                            onChange={onChange}
                            className={classNames(
                              isDirty === true && value.length > 0 && 'valid',
                            )}
                            inputclassName={classNames(
                              Model?.VerifyIdentityViewModelWithoutReference
                                ?.FormModel.SurnameHasError && styles.hasError,
                            )}
                            onBlur={(e) =>
                              formProgression(e, 'Registration', 'Surname')
                            }
                          />
                          <input
                            type="hidden"
                            {...register(
                              'VerifyIdentityFormModel.IsDisableRecaptcha',
                            )}
                            value={
                              Model?.VerifyIdentityViewModelWithoutReference
                                ?.FormModel?.IsDisableRecaptcha
                            }
                          />
                        </>
                      );
                    }}
                  />
                  {Model?.VerifyIdentityViewModelWithoutReference?.FormModel
                    .SurnameHasError && (
                      <span className="Text--small text-danger">
                        {
                          Model?.VerifyIdentityViewModelWithoutReference?.PageData
                            ?.SurnameErrorMessage
                        }
                      </span>
                    )}
                </div>
                <div>
                  <Controller
                    control={control}
                    name={'VerifyIdentityFormModel.PersonalIdentifier'}
                    render={({
                      field: { onChange, name, value },
                      fieldState: { isDirty },
                    }) => {
                      if (isDirty) {
                        setValuesWhenIsDirty(name, value);
                        setShowHiddenInput(true);
                      }

                      return (
                        <>
                          <RegisterFormInput
                            name={name}
                            value={value}
                            label={
                              Model?.VerifyIdentityViewModelWithoutReference
                                ?.PageData?.PersonalIdentifierLabel
                            }
                            placeholder={
                              Model?.VerifyIdentityViewModelWithoutReference
                                ?.PageData?.PersonalIdentifierPlaceholderText
                            }
                            onChange={onChange}
                            className={classNames(
                              isDirty === true && value.length > 0 && 'valid',
                            )}
                            inputclassName={classNames(
                              Model?.VerifyIdentityViewModelWithoutReference
                                ?.FormModel.PersonalIdentifierHasError &&
                              styles.hasError,
                            )}
                            onBlur={(e) =>
                              formProgression(
                                e,
                                'Registration',
                                'PersonalIdentifier',
                              )
                            }
                          />
                        </>
                      );
                    }}
                  />
                  {Model?.VerifyIdentityViewModelWithoutReference?.FormModel
                    .PersonalIdentifierHasError && (
                      <span className="Text--small text-danger">
                        {
                          Model?.VerifyIdentityViewModelWithoutReference?.PageData
                            ?.PersonalIdentifierErrorMessage
                        }
                      </span>
                    )}
                </div>
              </>
            )}
            {Model.ShowNameSurNamePeselSection && (
              <>
                <div>
                  <Controller
                    control={control}
                    name={'VerifyIdentityFormModel.Name'}
                    render={({
                      field: { onChange, name, value },
                      fieldState: { isDirty },
                    }) => {
                      if (isDirty) {
                        setValuesWhenIsDirty(name, value);
                        setShowHiddenInput(true);
                      }

                      return (
                        <>
                          <RegisterFormInput
                            name={name}
                            value={value}
                            label={
                              Model?.VerifyIdentityViewModelWithoutReference
                                ?.PageData?.NameLabel
                            }
                            placeholder={
                              Model?.VerifyIdentityViewModelWithoutReference
                                ?.PageData?.NamePlaceholderText
                            }
                            onChange={onChange}
                            className={classNames(
                              isDirty === true && value.length > 0 && 'valid',
                            )}
                            inputclassName={classNames(
                              Model?.VerifyIdentityViewModelWithoutReference
                                ?.FormModel.NameHasError && styles.hasError,
                            )}
                            onBlur={(e) =>
                              formProgression(e, 'Registration', 'Name')
                            }
                          />
                          <input
                            type="hidden"
                            {...register(
                              'VerifyIdentityFormModel.IsDisableRecaptcha',
                            )}
                            value={
                              Model?.VerifyIdentityViewModelWithoutReference
                                ?.FormModel?.IsDisableRecaptcha
                            }
                          />
                        </>
                      );
                    }}
                  />
                  {Model?.VerifyIdentityViewModelWithoutReference?.FormModel
                    .NameHasError && (
                      <span className="Text--small text-danger">
                        {
                          Model?.VerifyIdentityViewModelWithoutReference?.PageData
                            ?.NameErrorMessage
                        }
                      </span>
                    )}
                </div>
                <div>
                  <Controller
                    control={control}
                    name={'VerifyIdentityFormModel.Surname'}
                    render={({
                      field: { onChange, name, value },
                      fieldState: { isDirty },
                    }) => {
                      if (isDirty) {
                        setValuesWhenIsDirty(name, value);
                        setShowHiddenInput(true);
                      }

                      return (
                        <>
                          <RegisterFormInput
                            name={name}
                            value={value}
                            label={
                              Model?.VerifyIdentityViewModelWithoutReference
                                ?.PageData?.SurnameLabel
                            }
                            placeholder={
                              Model?.VerifyIdentityViewModelWithoutReference
                                ?.PageData?.SurnamePlaceholderText
                            }
                            onChange={onChange}
                            className={classNames(
                              isDirty === true && value.length > 0 && 'valid',
                            )}
                            inputclassName={classNames(
                              Model?.VerifyIdentityViewModelWithoutReference
                                ?.FormModel.SurnameHasError && styles.hasError,
                            )}
                            onBlur={(e) =>
                              formProgression(e, 'Registration', 'Surname')
                            }
                          />
                          <input
                            type="hidden"
                            {...register(
                              'VerifyIdentityFormModel.IsDisableRecaptcha',
                            )}
                            value={
                              Model?.VerifyIdentityViewModelWithoutReference
                                ?.FormModel?.IsDisableRecaptcha
                            }
                          />
                        </>
                      );
                    }}
                  />
                  {Model?.VerifyIdentityViewModelWithoutReference?.FormModel
                    ?.SurnameHasError && (
                      <span className="Text--small text-danger">
                        {
                          Model?.VerifyIdentityViewModelWithoutReference?.PageData
                            ?.SurnameErrorMessage
                        }
                      </span>
                    )}
                </div>
                <div>
                  <Controller
                    control={control}
                    name={'VerifyIdentityFormModel.Pesel'}
                    render={({
                      field: { onChange, name, value },
                      fieldState: { isDirty },
                    }) => {
                      if (isDirty) {
                        setValuesWhenIsDirty(name, value);
                        setShowHiddenInput(true);
                      }

                      return (
                        <>
                          <RegisterFormInput
                            name={name}
                            value={value}
                            label={
                              Model?.VerifyIdentityViewModelWithoutReference
                                ?.PageData?.PeselLabel
                            }
                            placeholder={
                              Model?.VerifyIdentityViewModelWithoutReference
                                ?.PageData?.PeselPlaceholderText
                            }
                            onChange={onChange}
                            className={classNames(
                              isDirty === true && value.length > 0 && 'valid',
                            )}
                            inputclassName={classNames(
                              Model?.VerifyIdentityViewModelWithoutReference
                                ?.FormModel.PeselHasError && styles.hasError,
                            )}
                            onBlur={(e) =>
                              formProgression(e, 'Registration', 'Pesel')
                            }
                          />
                        </>
                      );
                    }}
                  />
                  {Model?.VerifyIdentityViewModelWithoutReference?.FormModel
                    ?.PeselHasError && (
                      <span className="Text--small text-danger">
                        {
                          Model?.VerifyIdentityViewModelWithoutReference?.PageData
                            ?.PeselErrorMessage
                        }
                      </span>
                    )}
                </div>
              </>
            )}
            {Model.ShowNameSurNamePostalCodeSection && (
              <>
                <div>
                  <Controller
                    control={control}
                    name={'VerifyIdentityFormModel.Surname'}
                    render={({
                      field: { onChange, name, value },
                      fieldState: { isDirty },
                    }) => {
                      if (isDirty) {
                        setValuesWhenIsDirty(name, value);
                        setShowHiddenInput(true);
                      }

                      return (
                        <>
                          <RegisterFormInput
                            name={name}
                            value={value}
                            label={
                              Model?.VerifyIdentityViewModelWithoutReference
                                ?.PageData?.SurnameLabel
                            }
                            placeholder={
                              Model?.VerifyIdentityViewModelWithoutReference
                                ?.PageData?.SurnamePlaceholderText
                            }
                            onChange={onChange}
                            className={classNames(
                              isDirty === true && value.length > 0 && 'valid',
                            )}
                            inputclassName={classNames(
                              Model?.VerifyIdentityViewModelWithoutReference
                                ?.FormModel.SurnameHasError && styles.hasError,
                            )}
                            onBlur={(e) =>
                              formProgression(e, 'Registration', 'Surname')
                            }
                          />
                          <input
                            type="hidden"
                            {...register(
                              'VerifyIdentityFormModel.IsDisableRecaptcha',
                            )}
                            value={
                              Model?.VerifyIdentityViewModelWithoutReference
                                ?.FormModel?.IsDisableRecaptcha
                            }
                          />
                        </>
                      );
                    }}
                  />
                  {Model?.VerifyIdentityViewModelWithoutReference?.FormModel
                    ?.SurnameHasError && (
                      <span className="Text--small text-danger">
                        {
                          Model?.VerifyIdentityViewModelWithoutReference?.PageData
                            ?.SurnameErrorMessage
                        }
                      </span>
                    )}
                </div>

                <div>
                  <div
                    className={
                      'Form-SelectWrapper--multi ' + styles.desktopDatePicker
                    }
                  >
                    <div className="form-group">
                      <label>
                        {
                          Model?.VerifyIdentityViewModelWithoutReference
                            ?.PageData?.DateofBirth
                        }
                      </label>
                      <div className={styles.selects} ref={DobRef}>
                        <Controller
                          control={control}
                          name={'VerifyIdentityFormModel.DateOfBirthDay'}
                          render={({
                            field: { onChange, name, value },
                            fieldState: { isDirty },
                          }) => {
                            if (isDirty) {
                              setValuesWhenIsDirty(name, value);
                            }
                            return (
                              <FormSelect
                                value={value}
                                placeholder={
                                  Model?.VerifyIdentityViewModelWithoutReference
                                    ?.PageData?.DayPlaceholder
                                }
                                options={daysOptions || []}
                                onChange={onChange}
                                name={name}
                                onBlur={(e) =>
                                  formProgression(
                                    e,
                                    'Registration',
                                    'DateOfBirthDay',
                                  )
                                }
                              />
                            );
                          }}
                        />
                        <Controller
                          control={control}
                          name={'VerifyIdentityFormModel.DateOfBirthMonth'}
                          render={({
                            field: { onChange, name, value },
                            fieldState: { isDirty },
                          }) => {
                            if (isDirty) {
                              setValuesWhenIsDirty(name, value);
                            }
                            return (
                              <FormSelect
                                value={value}
                                placeholder={
                                  Model?.VerifyIdentityViewModelWithoutReference
                                    ?.PageData?.MonthPlaceholder
                                }
                                options={monthOptions || []}
                                onChange={onChange}
                                name={name}
                                onBlur={(e) =>
                                  formProgression(
                                    e,
                                    'Registration',
                                    'DateOfBirthMonth',
                                  )
                                }
                              />
                            );
                          }}
                        />
                        <Controller
                          control={control}
                          name={'VerifyIdentityFormModel.DateOfBirthYear'}
                          render={({
                            field: { onChange, name, value },
                            fieldState: { isDirty },
                          }) => {
                            if (isDirty) {
                              setValuesWhenIsDirty(name, value);
                            }
                            return (
                              <FormSelect
                                value={value}
                                placeholder={
                                  Model?.VerifyIdentityViewModelWithoutReference
                                    ?.PageData?.YearPlaceholder
                                }
                                options={yearsOptions || []}
                                onChange={onChange}
                                name={name}
                                onBlur={(e) =>
                                  formProgression(
                                    e,
                                    'Registration',
                                    'DateOfBirthYear',
                                  )
                                }
                              />
                            );
                          }}
                        />
                      </div>
                      {(showDobValidation == true && isNotNullish == false && hasDOBError == true) &&
                        (<p className='error'>{Model?.VerifyIdentityViewModelWithoutReference
                        ?.PageData?.InvalidDateErrorText}</p>)}
                    </div>
                  </div>

                  <div className={styles.mobiledatapicker}>
                    <Controller
                      control={control}
                      name={'VerifyIdentityFormModel.DateOfBirth'}
                      render={({
                        field: { onChange, name, value },
                        fieldState: { isDirty },
                      }) => {
                        if (isDirty) {
                          setValuesWhenIsDirty(name, value);
                          setShowHiddenInput(true);
                        }

                        return (
                          <>
                            <RegisterFormInput
                              type="date"
                              name={name}
                              value={value}
                              label={
                                Model?.VerifyIdentityViewModelWithoutReference
                                  ?.PageData?.DateOfBirthLabel
                              }
                              placeholder="DD/MM/YYYY"
                              onChange={onChange}
                              max={format(new Date(Date.now()), 'yyyy-MM-dd')}
                              pattern="[0-9]*"
                              className={classNames(styles.widthfull)}
                              onBlur={(e) =>
                                formProgression(
                                  e,
                                  'Registration',
                                  'DateOfBirth',
                                )
                              }
                            />
                          </>
                        );
                      }}
                    />
                  </div>
                </div>
                <div>
                  <Controller
                    control={control}
                    name={'VerifyIdentityFormModel.PostalCode'}
                    render={({
                      field: { onChange, name, value },
                      fieldState: { isDirty },
                    }) => {
                      if (isDirty) {
                        setValuesWhenIsDirty(name, value);
                        setShowHiddenInput(true);
                      }

                      return (
                        <>
                          <RegisterFormInput
                            name={name}
                            value={value}
                            label={
                              Model?.VerifyIdentityViewModelWithoutReference
                                ?.PageData?.PostalCodeLabel
                            }
                            placeholder={
                              Model?.VerifyIdentityViewModelWithoutReference
                                ?.PageData?.PostalCodePlaceholderText
                            }
                            onChange={onChange}
                            className={classNames(
                              'w-100 ',
                              isDirty === true && value.length > 0 && 'valid',
                            )}
                            inputclassName={classNames(
                              Model?.VerifyIdentityViewModelWithoutReference
                                ?.FormModel.PostalCodeHasError &&
                              styles.hasError,
                            )}
                            onBlur={(e) =>
                              formProgression(e, 'Registration', 'PostalCode')
                            }
                          />
                        </>
                      );
                    }}
                  />
                  {Model?.VerifyIdentityViewModelWithoutReference?.FormModel
                    .PostalCodeHasError && (
                      <span className="Text--small text-danger">
                        {
                          Model?.VerifyIdentityViewModelWithoutReference?.PageData
                            ?.PostalCodeErrorMessage
                        }
                      </span>
                    )}
                </div>
              </>
            )}
            {showTermsAndConditionsLink && (
              <div
                className="form-group u-textCenter terms js-modal"
                id="TermsAndConditionsLinkHidden"
              >
                <p className="form-text">
                  {
                    Model?.VerifyIdentityViewModelWithoutReference?.PageData
                      ?.ContinueAcceptfText
                  }{' '}
                  <span className="form-text-bold">
                    <a
                      href={
                        Model?.VerifyIdentityViewModelWithoutReference
                          ?.TermsAndConditionsPageUrl
                      }
                      target="_blank"
                      className="TermsAndConditionsPolicy form-text-bold u-textsmall"
                    >
                      {
                        Model?.VerifyIdentityViewModelWithoutReference?.PageData
                          ?.TermsOfUse
                      }
                    </a>
                  </span>{' '}
                  {
                    Model?.VerifyIdentityViewModelWithoutReference?.PageData
                      ?.OurTextLabel
                  }{' '}
                  <span className="form-text-bold">
                    <a
                      href={
                        Model?.VerifyIdentityViewModelWithoutReference
                          ?.PrivacyPolicyPageUrl
                      }
                      target="_blank"
                      className="TermsAndConditionsPolicy form-text-bold u-textsmall"
                    >
                      {
                        Model?.VerifyIdentityViewModelWithoutReference?.PageData
                          ?.PrivacyPolicyText
                      }
                    </a>
                  </span>
                </p>
              </div>
            )}
            {Model?.VerifyIdentityViewModelWithoutReference?.PageData
              ?.ShowAdditionalConsent &&
              showAdditionalConsent && (
                <div
                  id="additionalConsent"
                  className="u-marginBxmd grey-container-label"
                >
                  <label className="Checkbox u-marginBz u-marginRz">
                    <Controller
                      control={control}
                      name={
                        'VerifyIdentityFormModel.AcceptContactDetailsConsent'
                      }
                      render={({ field: { onChange, name, value } }) => (
                        <input
                          type="checkbox"
                          name={name}
                          value={value}
                          placeholder={
                            Model?.VerifyIdentityViewModelWithoutReference
                              ?.PageData?.AdditionalConsentText
                          }
                          onChange={onChange}
                          className="Checkbox__input"
                          checked={value}
                          defaultChecked={false}
                        />
                      )}
                    />
                    <span className="Checkbox__box"></span>
                    <span
                      id="ConsentCheckboxLabel"
                      className="Checkbox__label"
                      dangerouslySetInnerHTML={{
                        __html:
                          Model?.VerifyIdentityViewModelWithoutReference
                            ?.PageData.AdditionalConsentText,
                      }}
                    />
                  </label>
                </div>
              )}
            <div className="u-marginBxmd u-textCenter">
              <button
                id="ContinueButton"
                type="submit"
                className={classNames(
                  'Button w-100',
                  styles.submitBtn,
                  disableContinueButton === true && 'is-disabled',
                )}
              >
                {
                  Model?.VerifyIdentityViewModelWithoutReference?.PageData
                    ?.VerifyIdentityContinueButtonLabel
                }
              </button>
              <input
                type="hidden"
                {...register('VerifyIdentityFormModel.IsDisableRecaptcha')}
                value={
                  Model?.VerifyIdentityViewModelWithoutReference?.FormModel
                    ?.IsDisableRecaptcha
                }
              />
            </div>
          </div>
        </div>
      </form>
      {Model?.VerifyIdentityViewModelWithoutReference
        ?.ShowAlreadyRegisteredPanel &&
        showAlreadyRegisteredPanel && (
          <>
            <AlreadyRegisteredPanel
              onClose={() => setShowAlreadyRegisteredPanel(false)}
              {...props}
            />
          </>
        )}

      {Model?.VerifyIdentityViewModelWithoutReference
        ?.RegistrationDetailsIncorrectPanel &&
        showRegistrationDetailsIncorrect && (
          <>
            <RegistrationDetailsIncorrectPanel
              onClose={() => {
                setShowRegistrationDetailsIncorrect(false);
              }}
              {...props}
            />
          </>
        )}
      {showUnknownErrorPanel && (
        <>
          <ShowUnknownErrorPanel
            onClose={() => setShowUnknownErrorPanel(false)}
            {...props}
          />
        </>
      )}
      {showCustomerNotAuthorizedPanel && (
        <>
          <CustomerNotAuthorized
            onClose={() => setShowCustomerNotAuthorizedPanel(false)}
            {...props}
          />
        </>
      )}
      {Model?.VerifyIdentityViewModelWithoutReference?.showRecaptchaError && (
        <div className="form-text text-danger">
          {PageData?.ReCaptchaErrorMessage}
        </div>
      )}
      <div></div>
    </div>
  );
};

export default DebtSection;
